import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useCan } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    ClipboardList, Search, RefreshCw, User, Eye, ExternalLink,
    MapPin, Phone, Calendar as CalendarIcon, FileText, PhoneCall,
    PhoneIncoming, PhoneOutgoing, CircleDollarSign, History, Trash2
} from 'lucide-react';
import InventaireTempsEnvoi from '../components/InventaireTempsEnvoi';
import HistoriqueModal from '../components/HistoriqueModal';
import { Toaster, toast } from 'sonner';
import { format, parseISO, isValid, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_CONFIG = {
    nouveau: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700' },
    en_redaction: { label: 'En redaction', color: 'bg-amber-100 text-amber-700' },
    termine: { label: 'Termine', color: 'bg-emerald-100 text-emerald-700' },
};

const TYPE_CONFIG = {
    visite_presentiel: { label: 'Presentiel', color: 'bg-[#1B365D] text-white' },
    visite_virtuelle: { label: 'Visite virtuelle', color: 'bg-[#C9A962] text-white' },
};

function getRelativeDate(dateStr, prefix) {
    if (!dateStr) return '';
    try {
        const d = parseISO(dateStr);
        if (!isValid(d)) return dateStr;
        return prefix + ' ' + formatDistanceToNow(d, { locale: fr, addSuffix: false });
    } catch { return dateStr; }
}

function getEventDateDisplay(item) {
    // Show the creation date of the inventaire card
    if (item.created_at) {
        try {
            const d = parseISO(item.created_at);
            if (!isValid(d)) return item.created_at;
            return 'Cree le ' + format(d, 'dd/MM/yyyy HH:mm', { locale: fr });
        } catch { return item.created_at; }
    }
    return '';
}

function getTypeLabel(item) {
    if (item.event_type) {
        const cfg = TYPE_CONFIG[item.event_type];
        return cfg ? cfg.label : item.event_type;
    }
    return 'Listing';
}

import CallsBadge from '../components/CallsBadge';

function getTypeBadge(item) {
    if (item.event_type) {
        const cfg = TYPE_CONFIG[item.event_type] || { label: item.event_type, color: 'bg-gray-100 text-gray-700' };
        return <Badge className={`${cfg.color} text-xs`}>{cfg.label}</Badge>;
    }
    return <Badge className="bg-violet-100 text-violet-700 text-xs">Listing</Badge>;
}

function getCallsBadge(item) {
    return <CallsBadge item={item} />;
}

export default function InventaireList() {
    const navigate = useNavigate();
    const { canWrite: canWriteDevis } = useCan('devis');
    const { canDelete: canDeleteProspect } = useCan('prospects');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatut, setFilterStatut] = useState('all');
    const [historyTarget, setHistoryTarget] = useState(null);
    // Quick filter: "Inventaire terminé + sans devis" → spots rows where the
    // commercial finished the inventory but never clicked "Creer un devis".
    // Pure client-side toggle, no extra backend params.
    const [onlyAwaitingDevis, setOnlyAwaitingDevis] = useState(false);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterStatut !== 'all') params.append('statut', filterStatut);
            if (search) params.append('search', search);
            const res = await axios.get(`${API_URL}/api/inventaire-prospects?${params}`, { withCredentials: true });
            setItems(res.data || []);
        } catch { toast.error('Erreur chargement'); }
        finally { setLoading(false); }
    }, [filterStatut, search]);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    // Apply the "awaiting devis" client-side filter on top of the
    // server-filtered list. Criteria match what the user sees in the row:
    //   - statut_inventaire === 'termine'   (green "Termine" badge)
    //   - has_devis === false               (still shows "Creer un devis")
    const displayedItems = onlyAwaitingDevis
        ? items.filter(it => it.statut_inventaire === 'termine' && !it.has_devis)
        : items;

    const handleCreateDevis = async (item, e) => {
        e.stopPropagation();
        // Optimistic UI: immediately mark this row as has_devis so the button
        // turns into a non-clickable red heart icon.
        setItems(prev => prev.map(it => it.id === item.id ? { ...it, has_devis: true } : it));
        try {
            await axios.post(`${API_URL}/api/devis`, { prospect_id: item.prospect_id }, { withCredentials: true });
            toast.success('Devis cree');
            navigate('/devis');
        } catch {
            toast.error('Erreur lors de la creation du devis');
            // rollback on error
            setItems(prev => prev.map(it => it.id === item.id ? { ...it, has_devis: false } : it));
        }
    };

    const handleDelete = async (item, e) => {
        e.stopPropagation();
        if (!window.confirm(`Supprimer la fiche inventaire de "${item.nom}" ? Elle sera déplacée dans la Corbeille. Le prospect et ses autres données (agenda, devis…) ne sont pas affectés.`)) return;
        try {
            // Scope of this trash button = the INVENTAIRE card only.
            // We deliberately do NOT call DELETE /api/prospects/{id} here
            // so the user can clean up an orphan inventaire row without
            // touching the underlying prospect / agenda / devis records.
            await axios.delete(`${API_URL}/api/inventaire-prospects/${item.id}`, { withCredentials: true });
            toast.success('Fiche inventaire déplacée dans la Corbeille');
            setItems(prev => prev.filter(it => it.id !== item.id));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const renderDevisAction = (item) => {
        if (item.has_devis) {
            return (
                <span
                    className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-emerald-50 border border-emerald-200 cursor-not-allowed"
                    title="Devis deja cree"
                    aria-disabled="true"
                    data-testid={`devis-created-success-${item.id}`}
                >
                    <CircleDollarSign className="w-4 h-4 text-emerald-600" />
                </span>
            );
        }
        if (item.statut_inventaire !== 'termine') return null;
        return (
            <Button
                variant="outline"
                size="sm"
                disabled={!canWriteDevis}
                title={!canWriteDevis ? 'Accès refusé : permission Écriture sur Devis requise' : 'Créer un devis'}
                className="h-7 text-xs gap-1.5 text-[#C9A962] border-[#C9A962]/30 hover:bg-[#C9A962]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={(e) => handleCreateDevis(item, e)}
                data-testid={`create-devis-${item.id}`}
            >
                <FileText className="w-3 h-3" /> Creer un devis
            </Button>
        );
    };

    return (
        <Layout>
            <Toaster position="top-right" richColors />
            <div data-testid="inventaire-list-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-5 gap-3">
                    <div className="flex items-center gap-3">
                        <ClipboardList className="w-6 h-6 text-[#1B365D]" />
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="inventaire-title">
                                Inventaire
                            </h1>
                            <p className="text-xs text-gray-500 mt-0.5">{displayedItems.length} element{displayedItems.length !== 1 ? 's' : ''}{onlyAwaitingDevis ? ' (termine sans devis)' : ''}</p>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchItems} disabled={loading} className="gap-1.5 h-8 text-xs" data-testid="refresh-inventaire">
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                        Actualiser
                    </Button>
                </div>

                {/* Stats — temps moyen d'envoi des devis par utilisateur */}
                <div className="mb-4">
                    <InventaireTempsEnvoi />
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input placeholder="Rechercher par nom, tel, email..." value={search} onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 h-9 bg-white text-sm" data-testid="search-inventaire" />
                    </div>
                    <Select value={filterStatut} onValueChange={setFilterStatut}>
                        <SelectTrigger className="w-44 h-9 text-sm" data-testid="filter-statut-inventaire">
                            <SelectValue placeholder="Tous les statuts" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les statuts</SelectItem>
                            <SelectItem value="nouveau">Nouveau</SelectItem>
                            <SelectItem value="en_redaction">En redaction</SelectItem>
                            <SelectItem value="termine">Termine</SelectItem>
                        </SelectContent>
                    </Select>
                    {/* Quick toggle: terminé + sans devis (clients ayant fini l'inventaire mais pas encore de devis créé) */}
                    <Button
                        type="button"
                        variant={onlyAwaitingDevis ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setOnlyAwaitingDevis(v => !v)}
                        className={`h-9 text-xs gap-1.5 ${onlyAwaitingDevis ? 'bg-[#C9A962] hover:bg-[#b8985a] text-white border-[#C9A962]' : 'text-[#C9A962] border-[#C9A962]/40 hover:bg-[#C9A962]/10'}`}
                        title="N'afficher que les inventaires terminés dont le devis n'a pas encore été créé"
                        data-testid="filter-awaiting-devis"
                    >
                        <FileText className="w-3.5 h-3.5" />
                        Termine sans devis
                    </Button>
                </div>

                {/* Content */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                    ) : displayedItems.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                            <ClipboardList className="h-10 w-10 mb-2 text-gray-300" />
                            <p className="text-sm font-medium">{onlyAwaitingDevis ? 'Aucun inventaire termine sans devis' : 'Aucun inventaire'}</p>
                            <p className="text-xs mt-1">{onlyAwaitingDevis ? 'Tous les inventaires termines ont deja un devis cree' : 'Les inventaires apparaissent automatiquement quand un prospect passe en Devis estimatif, Listing telephone ou Listing email'}</p>
                        </div>
                    ) : (
                        <>
                        {/* Mobile Cards */}
                        <div className="block md:hidden space-y-2 p-2">
                            {displayedItems.map((item) => {
                                const sCfg = STATUT_CONFIG[item.statut_inventaire] || STATUT_CONFIG.nouveau;
                                return (
                                    <div key={item.id} className="rounded-lg p-3 border border-gray-200 bg-gray-50/50 space-y-2 cursor-pointer hover:bg-gray-100/50 transition-colors"
                                        onClick={() => navigate(`/prospects/${item.prospect_id}?context=extended&from=inventaire`)} data-testid={`inv-card-${item.id}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="h-8 w-8 rounded-full bg-[#1B365D]/10 flex items-center justify-center shrink-0">
                                                    <User className="h-4 w-4 text-[#1B365D]" />
                                                </div>
                                                <span className="font-semibold text-sm text-[#1B365D] truncate">{item.nom}</span>
                                            </div>
                                            <Badge className={`${sCfg.color} text-xs shrink-0`}>{sCfg.label}</Badge>
                                        </div>
                                        {item.statut_inventaire === 'en_redaction' && item.block_reason && (
                                            <p className="text-[11px] text-amber-700 leading-tight">
                                                ⚠ {item.block_reason}
                                            </p>
                                        )}
                                        <div className="flex items-center justify-between text-xs text-gray-500">
                                            <span>{getTypeBadge(item)}</span>
                                            <span>{getEventDateDisplay(item)}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            {getCallsBadge(item)}
                                        </div>
                                        <div className="flex items-center justify-end gap-2">
                                            {renderDevisAction(item)}
                                            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-gray-600"
                                                onClick={(e) => { e.stopPropagation(); setHistoryTarget({ prospectId: item.prospect_id, nom: item.nom }); }}>
                                                <History className="w-3 h-3" /> Historique
                                            </Button>
                                            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-[#1B365D]"
                                                onClick={(e) => { e.stopPropagation(); navigate(`/prospects/${item.prospect_id}?from=inventaire`); }}>
                                                <Eye className="w-3 h-3" /> Prospect
                                            </Button>
                                            <Button variant="ghost" size="sm"
                                                className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-red-400 disabled:hover:bg-transparent"
                                                disabled={!canDeleteProspect}
                                                onClick={(e) => handleDelete(item, e)}
                                                title={!canDeleteProspect ? 'Accès refusé : permission Suppression requise' : 'Supprimer'}
                                                data-testid={`delete-inv-card-${item.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Desktop Table */}
                        <div className="hidden md:block overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-[#1B365D]/[0.03]">
                                        <TableHead className="font-semibold text-[#1B365D]">Client</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Commercial</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Type</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Date</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Appels</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]">Statut</TableHead>
                                        <TableHead className="font-semibold text-[#1B365D]"></TableHead>
                                        <TableHead className="font-semibold text-[#1B365D] text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {displayedItems.map((item) => {
                                        const sCfg = STATUT_CONFIG[item.statut_inventaire] || STATUT_CONFIG.nouveau;
                                        return (
                                            <TableRow key={item.id} data-testid={`inv-row-${item.id}`}
                                                className={`transition-colors cursor-pointer hover:bg-gray-50 ${item.statut_inventaire === 'termine' ? 'bg-emerald-50/50' : item.statut_inventaire === 'nouveau' ? 'bg-blue-50/30' : 'bg-amber-50/30'}`}
                                                onClick={() => navigate(`/prospects/${item.prospect_id}?context=extended&from=inventaire`)}>                                                <TableCell>
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="h-9 w-9 rounded-full bg-[#1B365D]/10 flex items-center justify-center shrink-0">
                                                            <User className="h-4 w-4 text-[#1B365D]" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-semibold text-sm text-[#1B365D] truncate">{item.nom}</p>
                                                            {item.telephone && <p className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{item.telephone}</p>}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm text-gray-600">{item.commercial_name || '-'}</TableCell>
                                                <TableCell>{getTypeBadge(item)}</TableCell>
                                                <TableCell>
                                                    <span className="text-sm text-gray-600">{getEventDateDisplay(item)}</span>
                                                </TableCell>
                                                <TableCell>
                                                    {getCallsBadge(item)}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={`${sCfg.color} text-xs`}>
                                                        {sCfg.label}
                                                    </Badge>
                                                    {item.statut_inventaire === 'en_redaction' && item.block_reason && (
                                                        <p className="mt-1 text-[11px] text-amber-700 leading-tight max-w-[180px]" title={item.block_reason}>
                                                            ⚠ {item.block_reason}
                                                        </p>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {renderDevisAction(item)}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex justify-end gap-1.5">
                                                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-gray-600 hover:text-[#C9A962] hover:bg-[#C9A962]/10"
                                                            onClick={(e) => { e.stopPropagation(); setHistoryTarget({ prospectId: item.prospect_id, nom: item.nom }); }}
                                                            data-testid={`history-btn-${item.id}`}
                                                            title="Voir l'historique">
                                                            <History className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 text-[#1B365D] border-[#1B365D]/20 hover:bg-[#1B365D]/5"
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/prospects/${item.prospect_id}?context=extended&from=inventaire`); }} data-testid={`view-prospect-${item.id}`}>
                                                            <ExternalLink className="w-3 h-3" /> Apercu
                                                        </Button>
                                                        <Button variant="ghost" size="icon"
                                                            className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-red-400 disabled:hover:bg-transparent"
                                                            disabled={!canDeleteProspect}
                                                            onClick={(e) => handleDelete(item, e)}
                                                            title={!canDeleteProspect ? 'Accès refusé : permission Suppression requise' : 'Supprimer (déplacer dans la corbeille)'}
                                                            data-testid={`delete-inv-${item.id}`}>
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                        </>
                    )}
                </div>
            </div>
            <HistoriqueModal
                open={!!historyTarget}
                onOpenChange={(o) => !o && setHistoryTarget(null)}
                prospectId={historyTarget?.prospectId}
                title={historyTarget?.nom}
            />
        </Layout>
    );
}
